import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, wanneer, geleden } from '../i18n.js';

/**
 * Beheer › SOC-schermen: een sleutel per wandscherm, met alleen leesrecht, voor de klanten die het
 * toont. Zie docs/besluiten/soc-scherm.md. **De link met de sleutel is één keer te zien**; daarna staat
 * alleen het begin ervan hier, om hem te herkennen. Kwijt is opnieuw maken en de oude intrekken.
 */
export default function BeheerSchermen({ klanten }) {
  const client = useQueryClient();
  const q = useQuery({ queryKey: ['schermen'], queryFn: api.schermen });
  const [naam, zetNaam] = useState('');
  const [kies, zetKies] = useState(klanten.map((k) => k.code));
  const [nieuw, zetNieuw] = useState(null);
  const [fout, zetFout] = useState(null);
  const [gekopieerd, zetGekopieerd] = useState(false);

  const maak = async (e) => {
    e.preventDefault(); zetFout(null); zetGekopieerd(false);
    try {
      const { data } = await api.maakScherm({ name: naam.trim(), tenants: kies });
      zetNieuw(data); zetNaam('');
      await client.invalidateQueries({ queryKey: ['schermen'] });
    } catch (err) { zetFout(t(err.details ? Object.values(err.details)[0] : err.message)); }
  };
  const trekIn = async (s) => {
    zetFout(null);
    try { await api.trekSchermIn(s.id); await client.invalidateQueries({ queryKey: ['schermen'] }); }
    catch (err) { zetFout(t(err.message)); }
  };

  return (
    <>
      {fout && <div className="melder rood" style={{ marginBottom: 14 }}>{fout}</div>}
      {nieuw && (
        <div className="callout ok" style={{ marginBottom: 14 }}>
          <b>{t('De link voor {naam}, één keer te zien:', { naam: nieuw.name ?? t('het scherm') })}</b>
          <div className="rij" style={{ marginTop: 8 }}>
            <input className="input mono" readOnly value={nieuw.url} style={{ flex: 1, minWidth: 0 }} onFocus={(e) => e.target.select()} aria-label={t('Link voor het scherm')} />
            <button type="button" className="btn" onClick={() => navigator.clipboard?.writeText(nieuw.url).then(() => zetGekopieerd(true))}>{gekopieerd ? t('Gekopieerd') : t('Kopiëren')}</button>
          </div>
          <p className="noot">{t('Open hem op het apparaat bij het wandscherm. ADTS ZERO zet de sleutel in een cookie en haalt hem uit het adres. Zet de browser daar in kioskmodus.')}</p>
        </div>
      )}
      <section className="card">
        <header><h2>{t('Schermen')}</h2></header>
        {q.isPending ? <p className="muted">{t('Laden…')}</p> : q.data?.data.length ? (
          <table>
            <thead><tr><th>{t('Naam')}</th><th>{t('Klanten')}</th><th>{t('Sleutel')}</th><th>{t('Laatst gezien')}</th><th /></tr></thead>
            <tbody>{q.data.data.map((s) => (
              <tr key={s.id} style={s.revokedAt ? { opacity: 0.55 } : undefined}>
                <td><b>{s.name}</b><br /><span className="muted" style={{ fontSize: 12 }}>{t('gemaakt {wanneer}', { wanneer: wanneer(s.createdAt) })}</span></td>
                <td>{s.tenants.join(', ')}</td>
                <td className="mono">zk_{s.prefix}_…</td>
                <td className="muted">{s.revokedAt ? t('ingetrokken {wanneer}', { wanneer: wanneer(s.revokedAt) }) : s.lastUsedAt ? geleden(s.lastUsedAt) : t('nog niet')}</td>
                <td>{!s.revokedAt && <button type="button" className="namelink gevaar" onClick={() => trekIn(s)}>{t('Intrekken')}</button>}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : <p className="empty">{t('Nog geen schermen.')}</p>}
      </section>
      <section className="card" style={{ marginTop: 18 }}>
        <header><h2>{t('Scherm toevoegen')}</h2></header>
        <form className="form" onSubmit={maak}>
          <label className="field"><span>{t('Naam')}</span>
            <input className="input" value={naam} onChange={(e) => zetNaam(e.target.value)} placeholder="SOC-wand-01" /></label>
          <div className="field"><span>{t('Klanten op dit scherm')}</span>
            <div className="themarij" style={{ gap: 12 }}>{klanten.map((k) => (
              <label key={k.code} className="vink"><input type="checkbox" checked={kies.includes(k.code)}
                onChange={(e) => zetKies((v) => (e.target.checked ? [...v, k.code] : v.filter((x) => x !== k.code)))} />{k.name}</label>
            ))}</div></div>
          <div><button className="btn primary" disabled={naam.trim().length < 2 || !kies.length}>{t('Sleutel maken')}</button></div>
          <p className="noot">{t('Een sleutel mag alleen het SOC-scherm lezen, voor deze klanten. Elke verbinding staat in het logboek. Intrekken werkt meteen.')}</p>
        </form>
      </section>
    </>
  );
}
